import {
  redirectToSessionExpired,
  SessionEndReason,
} from '@src/lib/session-redirect';
import { createSelectors } from '@src/stores/create-selectors';
import { addSeconds } from 'date-fns';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type SessionStatus = 'alive' | 'warning' | 'dead';

type State = {
  sessionExpiresAt?: number;
  showAlert: boolean;
  timeoutIn: number;
  timeoutId?: NodeJS.Timeout;
  countdownTimeoutId?: NodeJS.Timeout;
  secondsUntilSessionExpires: number;
  sessionStatus: SessionStatus;
  lastProbeAt?: number;
  // The User this page load signed in as, sent as X-Max-User so the API can
  // reject writes once another User has signed in from a different tab.
  userUuid?: string;
  actions: Actions;
};

type Actions = {
  restartSessionTimeout: (duration?: number) => void;
  startCountdownTimer: () => void;
  markDead: (reason?: SessionEndReason) => void;
  setLastProbeAt: (timestamp: number) => void;
  rememberUser: (uuid: string) => boolean;
};

const defaultState: Omit<State, 'actions'> = {
  sessionExpiresAt: undefined, // Timestamp in milliseconds
  showAlert: false,
  timeoutIn: 900, // 15 minutes
  timeoutId: undefined,
  countdownTimeoutId: undefined,
  secondsUntilSessionExpires: 60,
  sessionStatus: 'alive',
  lastProbeAt: undefined,
  userUuid: undefined,
};

/**
 * Zustand store for the Session.
 */
const useSessionStoreBase = create<State>()(
  devtools(
    (set, getState) => ({
      ...defaultState,
      actions: {
        startCountdownTimer: () => {
          const { sessionExpiresAt } = getState();
          const secondsRemaining = sessionExpiresAt
            ? Math.max(0, Math.ceil((sessionExpiresAt - Date.now()) / 1000))
            : defaultState.secondsUntilSessionExpires;

          set(
            {
              showAlert: true,
              sessionStatus: 'warning',
              secondsUntilSessionExpires: secondsRemaining,
            },
            false,
            'startCountdownTimer',
          );
          const existingTimeout = getState().countdownTimeoutId;

          existingTimeout && clearTimeout(existingTimeout);

          const countdownTimeoutId = setInterval(() => {
            set((state) => ({
              secondsUntilSessionExpires: state.secondsUntilSessionExpires - 1,
            }));
            if (getState().secondsUntilSessionExpires === 0) {
              clearInterval(countdownTimeoutId);
            }
          }, 1000);

          set({ countdownTimeoutId }, false, 'setCountdownTimoutId');
        },
        restartSessionTimeout: (duration) => {
          const { timeoutIn, timeoutId, countdownTimeoutId } = getState();

          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          if (countdownTimeoutId) {
            clearTimeout(countdownTimeoutId);
          }

          if (duration) {
            set({ timeoutIn: duration });
          }

          const effectiveTimeoutIn = duration ?? timeoutIn;
          if (effectiveTimeoutIn) {
            const sessionExpiresAt = addSeconds(
              new Date(),
              effectiveTimeoutIn,
            ).getTime();

            set(
              {
                showAlert: false,
                sessionStatus: 'alive',
                sessionExpiresAt,
              },
              false,
              'restartSessionTimeout',
            );
            const countdownDelay = Math.max(
              0,
              sessionExpiresAt -
                Date.now() -
                defaultState.secondsUntilSessionExpires * 1000,
            );
            const newTimeoutId = setTimeout(() => {
              getState().actions.startCountdownTimer();
            }, countdownDelay);

            set({ timeoutId: newTimeoutId }, false, 'setTimeoutId');
          }
        },
        markDead: (reason) => {
          const { timeoutId, countdownTimeoutId } = getState();
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (countdownTimeoutId) {
            clearTimeout(countdownTimeoutId);
          }
          set({ showAlert: false, sessionStatus: 'dead' }, false, 'markDead');
          redirectToSessionExpired(reason);
        },
        setLastProbeAt: (timestamp) => {
          set({ lastProbeAt: timestamp }, false, 'setLastProbeAt');
        },
        // Locks in the first User this page load sees and reports whether
        // `uuid` is still that User. Never overwrite it: adopting another
        // tab's User on a later profile fetch would switch off the API's
        // X-Max-User check, and this tab's stale data could then be saved
        // into that User's account.
        rememberUser: (uuid) => {
          const { userUuid } = getState();
          if (!userUuid) {
            set({ userUuid: uuid }, false, 'rememberUser');
            return true;
          }
          return userUuid === uuid;
        },
      },
    }),
    { name: 'useSessionStoreState' },
  ),
);

export const useSessionStore = createSelectors(useSessionStoreBase);
